import { apiClient } from '../../../../core/api/client';

/**
 * Service Provider profile type
 */
export type ServiceProviderProfile = {
  id: string;
  userId: string;
  companyName: string;
  businessAddress?: string;
  latitude?: number;
  longitude?: number;
  vettingStatus: 'PENDING' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED';
  newClientAcceptance: boolean;
  calendlyUrl?: string;
  referralCode: string;
  serviceOfferings?: string;
  consultationCount: number;
  monthlyConsultations?: Record<string, number>;
  serviceRanges: ('IN_OFFICE' | 'VIRTUAL' | 'IN_HOME')[];
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    name: string;
    profilePhoto?: string;
    accountStatus: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
    kindeId?: string;
    firstName?: string;
    lastName?: string;
  };
  categories: any[];
  specialties: any[];
  expertise: any[];
  serviceAreas: any[];
  // Additional profile fields
  yearsInBusiness?: number;
  establishedYear?: number;
  phoneNumber?: string;
  websiteUrl?: string;
  heroMediaUrl?: string;
  mediaType?: 'image' | 'video';
  yearsOfExperience?: number;
  professionalTitle?: string;
  certifications?: string[];
  professionalLicenses?: string[];
  carePhilosophy?: string;
  whyThisWork?: string;
  targetAgeRange?: string;
  priceRangeIndicator?: string;
  priceDetailsText?: string;
  emergencyResponseAvailable?: boolean;
  emergencyResponseDetails?: string;
  hoursDescription?: string;
  nationalCoverage?: boolean;
  registeredStates?: string[];
  tagline?: string;
  companyOverview?: string;
  providerBio?: string;
  introductoryVideoUrl?: string;
  languages?: string[];
  continuingEducation?: string;
};

/**
 * Onboarding status derived from profile data
 */
export type OnboardingStatus = {
  isComplete: boolean;
  progress: number;
  missingFields: string[];
};

/**
 * Fetch provider profile with all relationships
 * 
 * @returns Promise<ServiceProviderProfile> Complete provider profile
 * @throws Error if request fails or user is not authenticated
 */
export async function fetchProviderProfile(): Promise<ServiceProviderProfile> {
  const response = await apiClient.get<ServiceProviderProfile>(
    '/service-providers/profile'
  );
  
  return response.data;
}

/**
 * Calculate onboarding status from profile data.
 * `isComplete` reflects whether the provider has been approved for onboarding:
 * vetting status must be APPROVED and account status must be ACTIVE.
 * `progress` and `missingFields` are calculated separately from required
 * profile fields to show profile completeness.
 *
 * @param profile - Service provider profile
 * @returns OnboardingStatus with a status-based completion flag, plus field-based progress percentage and missing fields
 */
export function calculateOnboardingStatus(
  profile: ServiceProviderProfile | undefined
): OnboardingStatus {
  if (!profile) {
    return {
      isComplete: false,
      progress: 0,
      missingFields: ['profile'],
    };
  }

  const requiredFields = [
    { name: 'companyName', value: profile.companyName },
    { name: 'businessAddress', value: profile.businessAddress },
    { name: 'serviceOfferings', value: profile.serviceOfferings },
    { name: 'categories', value: profile.categories?.length > 0 },
    { name: 'specialties', value: profile.specialties?.length > 0 },
    { name: 'serviceRanges', value: profile.serviceRanges?.length > 0 },
    { name: 'serviceAreas', value: profile.serviceAreas?.length > 0 },
  ];

  const missingFields = requiredFields
    .filter((field) => !field.value)
    .map((field) => field.name);

  const completedFields = requiredFields.length - missingFields.length;
  const progress = Math.round((completedFields / requiredFields.length) * 100);

  return {
    isComplete:
      profile.vettingStatus === 'APPROVED' &&
      profile.user?.accountStatus === 'ACTIVE',
    progress,
    missingFields,
  };
}

/**
 * Update Calendly URL for the provider (Feature 035)
 *
 * @param calendlyUrl - The new Calendly URL (or empty string to remove)
 * @returns Promise<ServiceProviderProfile> Updated provider profile
 * @throws Error if request fails or validation fails
 */
export async function updateCalendlyUrl(calendlyUrl: string): Promise<ServiceProviderProfile> {
  const response = await apiClient.patch<ServiceProviderProfile>(
    '/service-providers/profile/calendly-url',
    { calendlyUrl }
  );

  return response.data;
}

export type UpdateProfileData = {
  tagline?: string | null;
  providerBio?: string | null;
  companyOverview?: string | null;
  carePhilosophy?: string | null;
  whyThisWork?: string | null;
  targetAgeRange?: string | null;
  priceRangeIndicator?: string | null;
  priceDetailsText?: string | null;
  hoursDescription?: string | null;
  emergencyResponseAvailable?: boolean;
  emergencyResponseDetails?: string | null;
  phoneNumber?: string | null;
  websiteUrl?: string | null;
  languages?: string[];
  continuingEducation?: string | null;
  newClientAcceptance?: boolean;
  serviceOfferings?: string | null;
};

export async function updateProfile(data: UpdateProfileData): Promise<ServiceProviderProfile> {
  const response = await apiClient.patch<ServiceProviderProfile>(
    '/service-providers/profile',
    data
  );

  return response.data;
}

